import React, { useState, useEffect, useRef } from 'react';
import { stageAPI } from '../services/api';
import { teinteAvatar, initiales } from '../services/etudiantsAffichage';

/**
 * SUIVI DE STAGE — rechercher un étudiant dans TOUT le module (lot L1b, E169)
 * ==========================================================================
 * Maquette : la recherche à droite de la barre du haut (#ongletsHaut). Le tableau de bord unique a disparu :
 * c'est ce qui répond à « où en est Léa ? ». Un résultat par dossier : stage (un par processus), certification,
 * registre des apprentis — chacun dit OÙ il mène, et un clic y va.
 *
 * ⚠️ La recherche est temporisée (250 ms) et ne part qu'à partir de 2 caractères ; une réponse arrivée après
 * une saisie plus récente est ignorée (sinon la liste montrerait le résultat d'une recherche déjà quittée).
 */
const TYPES = {
    stagiaire:     { libelle: 'Stage',          classe: 'ok',     icone: 'fa-diagram-project' },
    certification: { libelle: 'Certification',  classe: 'bleu',   icone: 'fa-stamp' },
    apprenti:      { libelle: 'Alternance',     classe: 'violet', icone: 'fa-handshake' }
};

function StageRechercheEtudiant({ departementId, anneeId, onChoisir }) {
    const [texte, setTexte] = useState('');
    const [resultats, setResultats] = useState(null);
    const [ouvert, setOuvert] = useState(false);
    const [enCours, setEnCours] = useState(false);
    const derniere = useRef(0);

    useEffect(() => {
        const q = texte.trim();
        if (q.length < 2) { setResultats(null); return undefined; }
        const n = ++derniere.current;
        const minuteur = setTimeout(async () => {
            setEnCours(true);
            try {
                const r = await stageAPI.rechercherEtudiant(departementId, anneeId, q);
                if (n === derniere.current) setResultats(r.data.resultats || []);
            } catch (_) {
                if (n === derniere.current) setResultats([]);
            } finally {
                if (n === derniere.current) setEnCours(false);
            }
        }, 250);
        return () => clearTimeout(minuteur);
    }, [texte, departementId, anneeId]);

    const choisir = (r) => { setOuvert(false); setTexte(''); setResultats(null); onChoisir(r); };

    return (
        <div className="acc-recherche" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOuvert(false); }}>
            <i className={'fa-solid ' + (enCours ? 'fa-circle-notch stg-tourne' : 'fa-magnifying-glass')} />
            <input value={texte} placeholder="Rechercher un étudiant (tous les processus)…" aria-label="Rechercher un étudiant dans tout le module"
                   onChange={(e) => { setTexte(e.target.value); setOuvert(true); }} onFocus={() => setOuvert(true)}
                   onKeyDown={(e) => { if (e.key === 'Escape') { setOuvert(false); e.target.blur(); } }} />
            {ouvert && resultats && (
                <div className="acc-res" role="listbox" aria-label="Résultats">
                    {resultats.length === 0 && <div className="acc-res-vide">Aucun étudiant trouvé pour « {texte.trim()} ».</div>}
                    {resultats.map((r, i) => {
                        const t = TYPES[r.type];
                        const e = r.etudiant;
                        return (
                            <button key={r.type + (r.dossier_id || e.id) + '-' + i} type="button" className="acc-res-l" role="option" aria-selected="false"
                                    onClick={() => choisir(r)}>
                                <span className="acc-res-av" style={{ background: teinteAvatar(e.nom, e.prenom) }}>{initiales(e.nom, e.prenom)}</span>
                                <span className="acc-res-tx">
                                    <b>{e.nom} {e.prenom}</b>
                                    <span>{r.ou} · {r.etat}</span>
                                </span>
                                <span className={'acc-chip ' + t.classe}><i className={'fa-solid ' + t.icone} /> {t.libelle}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default StageRechercheEtudiant;
